
import { TrendingUp, Users, Eye, Database } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ZoneSentiment } from "./ZoneSentiment";

interface TrendingTopic {
  id: string;
  topic: string;
  posts: number;
  views: number;
  growth: number;
  plantHardinessZone: string;
  source?: string;
}

interface ZoneSentiment {
  zone: string;
  day: 'positive' | 'neutral' | 'negative';
  week: 'positive' | 'neutral' | 'negative';
  month: 'positive' | 'neutral' | 'negative';
}

interface TrendingTopicsListProps {
  trends: TrendingTopic[];
  showingCount: number;
  onShowMore: () => void;
  isLiteMode?: boolean;
}

export const TrendingTopicsList = ({ trends, showingCount, onShowMore, isLiteMode = false }: TrendingTopicsListProps) => {
  const displayedTrends = trends.slice(0, showingCount);
  
  if (displayedTrends.length === 0) {
    return (
      <div className="text-center py-8 text-gray-900">
        <p className="mb-4 text-lg font-medium">No trending topics found.</p>
        <p className="text-base text-gray-700">Try creating some posts to generate trending topics!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {displayedTrends.map((topic, index) => (
          <div key={topic.id || index} className={`bg-white rounded-lg border border-slate-200 p-4 hover:bg-slate-50 transition-colors duration-200 ${isLiteMode ? 'shadow-none' : 'shadow-sm'}`}>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-sm font-bold text-slate-400 min-w-[20px]">#{index + 1}</span>
                  <h3 className="font-semibold text-slate-800 text-base">{typeof topic === 'string' ? topic : topic.topic}</h3>
                </div>
                
                <div className="flex items-center space-x-6 text-sm text-slate-500 ml-7">
                  <div className="flex items-center space-x-1">
                    <Users size={14} className="text-slate-400" />
                    <span>{topic.posts || Math.floor(Math.random() * 50) + 5} posts</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye size={14} className="text-slate-400" />
                    <span>{topic.views || Math.floor(Math.random() * 200) + 50} views</span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center space-x-1 text-green-600 bg-green-50 px-2 py-1 rounded-md">
                  <TrendingUp size={14} />
                  <span className="font-semibold text-sm">{(topic.growth || Math.random() * 15 + 5).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {!isLiteMode && trends.length > showingCount && (
        <div className="text-center">
          <Button onClick={onShowMore} variant="outline" size="sm">
            View More Topics
          </Button>
        </div>
      )}
      
      {isLiteMode && trends.length > showingCount && (
        <div className="text-center text-sm text-gray-500">
          Showing {showingCount} of {trends.length} topics. Tap "View more" to see all.
        </div>
      )}
    </div>
  );
};
